import React, { useState } from "react";
import * as HeroIcons from "@heroicons/react/24/outline";
import { ColumnDef } from "@tanstack/react-table";

import { DataTableColumnHeader } from "components/ui/data-table-column-header";
import { createStandardHeader } from "components/ui/standard-column-header";
import { titleCase, upperCase } from "lib/utils";

import { CnDocumentModal } from "../in-transit/CnDocumentModal";

const CnNumberCell: React.FC<{ cnNumber: string }> = ({ cnNumber }) => {
    const [isDocModalOpen, setIsDocModalOpen] = useState(false);

    return (
        <div className="flex items-center space-x-2 w-[120px]">
            <span className="font-medium text-xs">{cnNumber}</span>

            <HeroIcons.DocumentTextIcon
                className="h-4 w-4 text-gray-500 cursor-pointer hover:text-gray-800"
                onClick={() => setIsDocModalOpen(true)}
            />

            <CnDocumentModal
                isOpen={isDocModalOpen}
                onClose={() => setIsDocModalOpen(false)}
                cnNumber={cnNumber}
            />
        </div>
    );
};

export interface InStockShipment {
    cn_number: string;
    cn_date: string;
    branch_name?: string;
    consignor: string;
    consignee: string;
    origin: string;
    destination: string;
    stock_status: string;
    stock_location?: string | null;
    stock_since?: string | null;
}

export const createColumns = (): ColumnDef<InStockShipment>[] => [
    {
        accessorKey: "cn_number",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="CN Number" />
        ),
        cell: ({ row }) => {
            const cnNumber = row.getValue("cn_number") as string;
            return <CnNumberCell cnNumber={cnNumber} />;
        },
        enableSorting: true,
        enableHiding: false,
    },
    {
        accessorKey: "cn_date",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="CN Date" />
        ),
        cell: ({ row }) => {
            const date = new Date(row.getValue("cn_date"));
            const formatted = date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
            });
            return <div className="w-[100px] text-xs">{formatted}</div>;
        },
        enableSorting: true,
    },
    {
        accessorKey: "branch_name",
        header: createStandardHeader("Booking Branch"),
        cell: ({ row }) => {
            const branch = row.getValue("branch_name") as string;
            return (
                <div className="max-w-[120px] truncate text-xs" title={branch}>
                    {branch ? titleCase(branch) : "-"}
                </div>
            );
        },
        enableSorting: true,
    },
    {
        accessorKey: "consignor",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Consignor" />
        ),
        cell: ({ row }) => {
            const consignor = row.getValue("consignor") as string;
            return (
                <div className="max-w-[150px] truncate text-xs" title={consignor}>
                    {upperCase(consignor)}
                </div>
            );
        },
        enableSorting: true,
    },
    {
        accessorKey: "consignee",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Consignee" />
        ),
        cell: ({ row }) => {
            const consignee = row.getValue("consignee") as string;
            return (
                <div className="max-w-[150px] truncate text-xs" title={consignee}>
                    {upperCase(consignee)}
                </div>
            );
        },
        enableSorting: true,
    },
    {
        accessorKey: "origin",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Origin" />
        ),
        cell: ({ row }) => {
            const origin = row.getValue("origin") as string;
            return (
                <div className="max-w-[120px] truncate text-xs" title={origin}>
                    {titleCase(origin)}
                </div>
            );
        },
        enableSorting: true,
    },
    {
        accessorKey: "destination",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Destination" />
        ),
        cell: ({ row }) => {
            const destination = row.getValue("destination") as string;
            return (
                <div className="max-w-[120px] truncate text-xs" title={destination}>
                    {titleCase(destination)}
                </div>
            );
        },
        enableSorting: true,
    },
    {
        accessorKey: "stock_status",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Stock Status" />
        ),
        cell: ({ row }) => {
            const status = row.getValue("stock_status") as string;
            const isOnHold = status === "On Hold";
            return (
                <div
                    className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs bg-orange-100 text-orange-600 border border-orange-200`}
                >
                    {status}
                </div>
            );
        },
        enableSorting: false,
    },
    {
        accessorKey: "stock_location",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Stock Location" />
        ),
        cell: ({ row }) => {
            const loc = row.getValue("stock_location") as string | null | undefined;
            return (
                <div className="max-w-[120px] truncate text-xs" title={loc || ""}>
                    {loc ? titleCase(loc) : "-"}
                </div>
            );
        },
        enableSorting: true,
    },
    {
        accessorKey: "stock_since",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Stock Since" />
        ),
        cell: ({ row }) => {
            const value = row.getValue("stock_since");
            if (!value) return <div className="text-xs">-</div>;

            const date = new Date(value as string);
            const formatted = date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
            });
            return <div className="text-xs">{formatted}</div>;
        },
        enableSorting: true
    },
    {
        id: "stock_days",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Stock Days" />
        ),
        cell: ({ row }) => {
            const stockSince = row.original.stock_since;
            if (!stockSince) return <div className="text-xs">-</div>;

            const sinceDate = new Date(stockSince);
            const today = new Date();

            const diffMs = today.getTime() - sinceDate.getTime();
            const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

            return <div className="text-xs">{days}</div>;
        },
    }
];